// src/pages/production/ProductionPage.tsx
//
// Production module — recipe + items + planning + production records.
// Replicated from the GeniBatch standalone app (C:/JSchwartz-TEMP/GeniBatch),
// trimmed down to Phase 1: Items + Recipes CRUD, both backed by per-tenant
// localStorage. Other sections (Dashboard / BOM / Planning / Production /
// Settings) are placeholders for Phase 2.
//
// Data source is hardcoded to `local` for Phase 1. Phase 2 wires Advantage
// (read-only sales/inventory) and a DuckDB-backed alternative, behind the
// same productionStorage surface.

import { useEffect } from 'react';
import {
  ArrowLeft, LayoutDashboard, Package, ChefHat, Boxes, CalendarRange,
  ClipboardList, Settings, HardDrive,
} from 'lucide-react';
import { useAuthContext } from '../../hooks/useAuth';
import { useSection } from '../../lib/sectionStore';
import { getItems, getRecipes, loadSampleData } from '../../services/productionStorage';
import { isTenantSeeded, markTenantSeeded } from '../../services/productionSeedData';
import ItemsTab from './ItemsTab';
import RecipesTab from './RecipesTab';
import ProductsTab from './ProductsTab';
import PlanningTab from './PlanningTab';
import DashboardTab from './DashboardTab';
import SettingsTab from './SettingsTab';
import PlaceholderTab from './PlaceholderTab';

interface ProductionPageProps {
  onBack: () => void;
}

type Section = 'dashboard' | 'items' | 'recipes' | 'products' | 'planning' | 'production' | 'settings';

interface NavEntry {
  id: Section;
  label: string;
  icon: React.ComponentType<{ size?: number }>;
  description: string;
  phase2?: boolean;
}

const NAV: NavEntry[] = [
  { id: 'dashboard',  label: 'Dashboard',  icon: LayoutDashboard, description: 'Overview metrics: items, recipes, average margin' },
  { id: 'items',      label: 'Items',      icon: Package,         description: 'Raw materials and ingredients with unit cost' },
  { id: 'recipes',    label: 'Recipes',    icon: ChefHat,         description: 'Batch recipes with ingredient components and yield' },
  { id: 'products',   label: 'Products',   icon: Boxes,           description: 'Finished products (BOM) — recipe + packaging' },
  { id: 'planning',   label: 'Planning',   icon: CalendarRange,   description: 'Forecast-driven production plan and shortages' },
  { id: 'production', label: 'Production', icon: ClipboardList,   description: 'Production records and labor logging — Phase 3', phase2: true },
  { id: 'settings',   label: 'Settings',   icon: Settings,        description: 'Data source, import / export, clear' },
];

export default function ProductionPage({ onBack }: ProductionPageProps) {
  const { branding, activeTenant, claims } = useAuthContext();
  const slug = activeTenant?.slug ?? claims?.tenant_slug ?? 'default';
  const [section, setSection] = useSection('production', 'dashboard') as [Section, (s: Section) => void];

  // First-visit seed: if this tenant has no Items AND no Recipes AND has never
  // been seeded before, load the sample dataset so the user can explore the
  // app immediately. Subsequent visits skip this (preserves user data even
  // after a manual "Clear all" — the seeded flag persists). Settings → Load
  // sample data button re-seeds explicitly.
  useEffect(() => {
    if (!slug || isTenantSeeded(slug)) return;
    const hasItems = getItems(slug).length > 0;
    const hasRecipes = getRecipes(slug).length > 0;
    if (!hasItems && !hasRecipes) {
      loadSampleData(slug, false);
    }
    markTenantSeeded(slug);
  }, [slug]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex flex-col">
      {/* Header */}
      <header className="border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:border-slate-600/50 transition-all"
              title="Back"
            >
              <ArrowLeft size={16} />
            </button>
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
              <ChefHat size={18} className="text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-white tracking-tight">Production</h1>
              <p className="text-[11px] text-slate-500 -mt-0.5">
                {branding?.company_name || 'GeniReports'} · recipe + planning module
              </p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2 text-xs text-slate-400">
            <HardDrive size={12} />
            <span>Local storage · tenant <span className="text-slate-200 font-mono">{slug}</span></span>
          </div>
        </div>
      </header>

      {/* Mobile: horizontal scrollable tab strip (hidden at md+ where the
          sidebar takes over). Renders from the same NAV array as the sidebar. */}
      <nav className="md:hidden border-b border-slate-800/50 px-4 py-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex items-center gap-1.5 w-max">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = section === n.id;
            return (
              <button
                key={n.id}
                onClick={() => setSection(n.id)}
                className={`shrink-0 px-3 py-2.5 rounded-lg flex items-center gap-1.5 text-sm whitespace-nowrap transition-all
                  ${active
                    ? 'bg-orange-500/10 border border-orange-500/30 text-orange-200'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/40 border border-transparent'}`}
              >
                <Icon size={14} />
                <span>{n.label}</span>
                {n.phase2 && (
                  <span className="text-[9px] uppercase tracking-wider text-slate-500 px-1.5 py-0.5 rounded bg-slate-800/60">P3</span>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Body */}
      <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 flex gap-6">
        {/* Top-level section nav is the global app sidebar now. */}

        {/* Content */}
        <main className="flex-1 min-w-0">
          {section === 'dashboard' && <DashboardTab slug={slug} />}
          {section === 'items'     && <ItemsTab slug={slug} />}
          {section === 'recipes'   && <RecipesTab slug={slug} />}
          {section === 'products'  && <ProductsTab slug={slug} />}
          {section === 'planning'  && <PlanningTab slug={slug} />}
          {section === 'settings'  && <SettingsTab slug={slug} />}
          {section === 'production' && (
            <PlaceholderTab
              title="Production Records"
              description="Phase 3: log actual production runs (recipe, qty produced, labor hours), feeds back into cost actuals."
            />
          )}
        </main>
      </div>
    </div>
  );
}
